<!--
  ChangelogDialog.vue
  
  Modal dialog displaying application version history and release notes.
  Fetches and parses CHANGELOG-USER.md to show user-friendly release information.
  
  Features:
  - Shows latest version prominently with detailed changes
  - Collapsible history section for older versions
  - Animated transitions for opening/closing
  - Loading and error states
  
  Usage:
  ```vue
  <ChangelogDialog ref="changelogDialog" />
  
  // Open programmatically
  changelogDialog.value?.open()
  ```
  
  @component
-->
<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-300 ease-out"
      leave-active-class="transition-opacity duration-200 ease-in"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto" @click.self="close">
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-black/50 backdrop-blur-sm" />

        <!-- Dialog Container -->
        <div class="flex min-h-full items-center justify-center p-4">
          <Transition
            enter-active-class="transition-all duration-300 ease-out"
            leave-active-class="transition-all duration-200 ease-in"
            enter-from-class="opacity-0 scale-95 translate-y-4"
            enter-to-class="opacity-100 scale-100 translate-y-0"
            leave-from-class="opacity-100 scale-100 translate-y-0"
            leave-to-class="opacity-0 scale-95 translate-y-4"
          >
            <div
              v-if="isOpen"
              class="relative bg-charcoal-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
              @click.stop
            >
              <!-- Header -->
              <ChangelogDialogHeader :current-version="currentVersion" @close="close" />

              <!-- Content -->
              <div class="overflow-y-auto max-h-[calc(80vh-180px)] px-6 py-6">
                <!-- Loading State -->
                <ChangelogLoadingState v-if="loading" />

                <!-- Error State -->
                <ChangelogErrorState v-else-if="error" :message="error" />

                <!-- Version Content -->
                <div v-else class="space-y-6">
                  <!-- Featured Version — always the most recent major/minor release -->
                  <ChangelogVersionCard
                    v-if="featuredVersion"
                    :version="featuredVersion"
                    :is-latest="featuredVersion === latestVersion"
                  />

                  <!-- Recent Build Updates — patch releases for the current minor series, collapsible -->
                  <div
                    v-if="recentBuildVersions.length > 0"
                    class="mt-2 pt-3 border-t border-charcoal-600"
                  >
                    <button
                      type="button"
                      class="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-charcoal-900 hover:bg-charcoal-700 transition-colors text-sm"
                      @click="showRecentBuilds = !showRecentBuilds"
                    >
                      <div class="flex items-center gap-2">
                        <svg
                          class="w-4 h-4 text-charcoal-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span class="font-semibold text-charcoal-200"> Recent Build Updates </span>
                        <span class="text-xs text-charcoal-400">
                          ({{ recentBuildVersions.length }}
                          {{ recentBuildVersions.length === 1 ? 'update' : 'updates' }})
                        </span>
                      </div>
                      <svg
                        class="w-4 h-4 text-charcoal-400 transition-transform duration-200"
                        :class="{ 'rotate-180': showRecentBuilds }"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    <Transition
                      enter-active-class="transition-all duration-300 ease-out"
                      leave-active-class="transition-all duration-200 ease-in"
                      enter-from-class="opacity-0 scale-y-95"
                      enter-to-class="opacity-100 scale-y-100"
                      leave-from-class="opacity-100 scale-y-100"
                      leave-to-class="opacity-0 scale-y-95"
                    >
                      <div v-if="showRecentBuilds" class="mt-4 space-y-6 origin-top">
                        <ChangelogVersionCard
                          v-for="version in recentBuildVersions"
                          :key="version.version"
                          :version="version"
                          :is-latest="false"
                        />
                      </div>
                    </Transition>
                  </div>

                  <!-- Version History — older major/minor releases, collapsible -->
                  <ChangelogHistorySection
                    v-if="olderVersions.length > 0"
                    :versions="olderVersions"
                    :show-history="showHistory"
                    @toggle="showHistory = !showHistory"
                  />

                  <!-- No Changes Message -->
                  <ChangelogEmptyState v-if="!featuredVersion && versions.length === 0" />
                </div>
              </div>

              <!-- Footer -->
              <div class="sticky bottom-0 bg-charcoal-900 px-6 py-3 border-t border-charcoal-600">
                <button
                  type="button"
                  class="w-full px-4 py-2 text-sm bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white font-medium rounded-lg transition-all duration-200 shadow-sm hover:shadow"
                  @click="close"
                >
                  Close
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
/**
 * ChangelogDialog - Main dialog component for displaying version history
 *
 * This component fetches and parses the CHANGELOG-USER.md file to display
 * user-friendly release notes. The changelog is parsed into structured
 * version objects with added, changed, and fixed sections.
 */
import { ref, computed } from 'vue'
import versionData from '../../../../version.json'
import { groupVersions, type VersionInfo } from '@/utils/changelog-version-classifier'
import { reportError } from '@/lib/error-reporting'
import { ChangelogVersionCard } from './changelog'
import ChangelogDialogHeader from './changelog/ChangelogDialogHeader.vue'
import ChangelogLoadingState from './changelog/ChangelogLoadingState.vue'
import ChangelogErrorState from './changelog/ChangelogErrorState.vue'
import ChangelogHistorySection from './changelog/ChangelogHistorySection.vue'
import ChangelogEmptyState from './changelog/ChangelogEmptyState.vue'

// ============================================
// Types
// ============================================

// VersionInfo is imported from changelog-version-classifier

// ============================================
// State
// ============================================

/** Whether the dialog is open */
const isOpen = ref(false)

/** Whether changelog is being fetched */
const loading = ref(false)

/** Error message if fetch fails */
const error = ref<string | null>(null)

/** Parsed version history */
const versions = ref<VersionInfo[]>([])

/** Whether history section is expanded */
const showHistory = ref(false)

/** Whether recent build updates section is expanded */
const showRecentBuilds = ref(false)

// ============================================
// Computed
// ============================================

/** Current application version from version.json */
const currentVersion = computed(() => versionData.version)

/** Latest version (most recent in the changelog, regardless of type) */
const latestVersion = computed(() => versions.value[0] || null)

/**
 * All three dialog sections derived from the version list.
 * Logic lives in changelog-version-classifier.ts (groupVersions).
 */
const versionGroups = computed(() => groupVersions(versions.value))

/** Featured version — always the most recent release */
const featuredVersion = computed(() => versionGroups.value.featured)

/** Recent build updates — minor/patch between featured and previous major */
const recentBuildVersions = computed(() => versionGroups.value.recentBuilds)

/** Older versions — from the previous major milestone onward (capped) */
const olderVersions = computed(() => versionGroups.value.older)

// ============================================
// Methods
// ============================================

/**
 * Opens the dialog and fetches changelog data
 */
const open = async () => {
  isOpen.value = true
  loading.value = true
  error.value = null
  showHistory.value = false
  showRecentBuilds.value = false

  try {
    const response = await fetch('/CHANGELOG-USER.md?v=' + versionData.version, {
      cache: 'no-store',
    })
    if (!response.ok) {
      throw new Error('Could not load changelog')
    }

    const changelogText = await response.text()
    versions.value = parseChangelog(changelogText)
  } catch (err) {
    error.value = 'Unable to load version history. Please try again later.'
    reportError(err, { where: 'ChangelogDialog.open' })
  } finally {
    loading.value = false
  }
}

/**
 * Closes the dialog
 */
const close = () => {
  isOpen.value = false
}

/**
 * Parses CHANGELOG.md markdown into structured version objects
 *
 * Expected format:
 * ```
 * ## [1.2.3] - 2025-11-15
 * ### Added
 * - New feature description
 * ### Changed
 * - Improvement description
 * ### Fixed
 * - Bug fix description
 * ```
 *
 * @param markdown - Raw changelog markdown content
 * @returns Array of parsed version information
 */
function parseChangelog(markdown: string): VersionInfo[] {
  const versionInfos: VersionInfo[] = []
  const lines = markdown.split('\n')

  let currentVersion: VersionInfo | null = null
  let currentSection: 'added' | 'changed' | 'fixed' | null = null

  for (const line of lines) {
    // Match version headers: ## [0.1.0] - 2025-11-11
    const versionMatch = line.match(/^##\s+\[(\d+\.\d+\.\d+)\]\s+-\s+(.+)/)
    if (versionMatch && versionMatch[1] && versionMatch[2]) {
      if (currentVersion) {
        versionInfos.push(currentVersion)
      }
      currentVersion = {
        version: versionMatch[1],
        date: versionMatch[2],
        added: [],
        changed: [],
        fixed: [],
      }
      currentSection = null
      continue
    }

    // Skip [Unreleased] section
    if (line.match(/^##\s+\[Unreleased\]/)) {
      if (currentVersion) {
        versionInfos.push(currentVersion)
      }
      currentVersion = null
      currentSection = null
      continue
    }

    if (!currentVersion) continue

    // Match section headers
    if (line.match(/^###\s+Added/i)) {
      currentSection = 'added'
      continue
    } else if (line.match(/^###\s+(Changed|Improved)/i)) {
      currentSection = 'changed'
      continue
    } else if (line.match(/^###\s+Fixed/i)) {
      currentSection = 'fixed'
      continue
    }

    // Match bullet points and convert to user-friendly text
    const bulletMatch = line.match(/^[-*]\s+(.+)/)
    if (bulletMatch && bulletMatch[1] && currentSection) {
      const text = makeUserFriendly(bulletMatch[1])
      if (text) {
        currentVersion[currentSection].push(text)
      }
    }
  }

  // Don't forget the last version
  if (currentVersion) {
    versionInfos.push(currentVersion)
  }

  return versionInfos
}

/**
 * Converts developer-speak to user-friendly language
 *
 * Transformations:
 * - Removes markdown formatting (backticks, bold)
 * - Replaces technical terms with user-friendly alternatives
 * - Capitalizes first letter
 * - Ensures sentence ends with punctuation
 *
 * @param text - Raw changelog entry text
 * @returns User-friendly version of the text
 */
function makeUserFriendly(text: string): string {
  // Remove markdown formatting
  text = text.replace(/`([^`]+)`/g, '$1')
  text = text.replace(/\*\*([^*]+)\*\*/g, '$1')

  // Convert common developer terms to user-friendly language
  const replacements: [RegExp, string][] = [
    [/^Add(ed)?\s+/i, 'Added '],
    [/^Fix(ed)?\s+/i, 'Fixed '],
    [/^Improve(d)?\s+/i, 'Improved '],
    [/^Update(d)?\s+/i, 'Updated '],
    [/^Refactor(ed)?\s+/i, 'Improved '],
    [/\s+component\s+/gi, ' '],
    [/\s+UI\s+/gi, ' interface '],
    [/\s+UX\s+/gi, ' user experience '],
    [/authentication/gi, 'login'],
    [/authorization/gi, 'permissions'],
    [/implement(ed)?\s+/gi, 'Added '],
    [/\s+API\s+/gi, ' system '],
  ]

  for (const [pattern, replacement] of replacements) {
    text = text.replace(pattern, replacement)
  }

  // Capitalize first letter
  text = text.charAt(0).toUpperCase() + text.slice(1)

  // Ensure it ends with a period
  if (!text.endsWith('.') && !text.endsWith('!') && !text.endsWith('?')) {
    text += '.'
  }

  return text
}

// ============================================
// Expose
// ============================================

defineExpose({
  open,
  close,
})
</script>
